import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
  api,
  type APIToken,
  type CalendarColor,
  type EnergyLevel,
  type EnergyLevelInput,
  type Label as LabelData,
  type Settings,
} from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ErrorAlert } from "@/components/error-alert";
import { IdealDayDonut } from "@/components/ideal-day-donut";
import { PageSkeleton } from "@/components/page-skeleton";
import { colorName } from "@/lib/domain";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function formatMinutes(total: number): string {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

function hexFor(colors: CalendarColor[], id: string): string | undefined {
  return colors.find((c) => c.id === id)?.hex;
}

export function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [colors, setColors] = useState<CalendarColor[]>([]);
  const [colorsError, setColorsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSettings(await api.settings());
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load Settings"));
    }
  }, []);

  useEffect(() => {
    void load();
    (async () => {
      try {
        setColors((await api.colors()).colors);
      } catch (err) {
        setColorsError(
          messageOf(err, "could not load Google Calendar colours"),
        );
      }
    })();
  }, [load]);

  if (error && !settings) return <ErrorAlert message={error} />;
  if (!settings) return <PageSkeleton />;

  return (
    <div className="grid gap-4">
      {error && <ErrorAlert message={error} />}
      <WakingHoursCard settings={settings} onSaved={setSettings} />
      <ProjectSessionCard settings={settings} onSaved={setSettings} />
      <EnergyLevelsCard levels={settings.energy_levels} onChanged={load} />
      <LabelsCard
        labels={settings.labels}
        colors={colors}
        colorsError={colorsError}
        onChanged={load}
      />
      <IdealDayCard
        key={settings.labels.map((l) => `${l.id}:${l.ideal_percent}`).join("|")}
        labels={settings.labels.filter((l) => l.system_role !== "external")}
        colors={colors}
        onSaved={setSettings}
      />
      <AssistantAccessCard />
    </div>
  );
}

// --- Waking hours ---

function WakingHoursCard({
  settings,
  onSaved,
}: {
  settings: Settings;
  onSaved: (next: Settings) => void;
}) {
  const [dayStart, setDayStart] = useState(settings.day_start);
  const [dayEnd, setDayEnd] = useState(settings.day_end);
  const [margin, setMargin] = useState(String(settings.task_margin_minutes));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    dayStart !== settings.day_start ||
    dayEnd !== settings.day_end ||
    Number(margin) !== settings.task_margin_minutes;

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await api.updateSettings({
          day_start: dayStart,
          day_end: dayEnd,
          project_session_minutes: settings.project_session_minutes,
          task_margin_minutes: Number(margin),
        }),
      );
    } catch (err) {
      setError(messageOf(err, "could not save"));
    }
    setBusy(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Waking hours</CardTitle>
        <CardDescription>
          Your waking day is {formatMinutes(settings.waking_minutes)} long. This
          is what Energy Levels and the Day chart measure against.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-4 sm:grid-cols-3"
          onSubmit={(ev) => void handleSubmit(ev)}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="day-start">Day start</Label>
            <Input
              id="day-start"
              type="time"
              value={dayStart}
              onChange={(e) => setDayStart(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="day-end">Day end</Label>
            <Input
              id="day-end"
              type="time"
              value={dayEnd}
              onChange={(e) => setDayEnd(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="task-margin">Margin after each task (min)</Label>
            <Input
              id="task-margin"
              type="number"
              min={0}
              max={60}
              value={margin}
              onChange={(e) => setMargin(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Transition time counted in the Day chart only: a 30 min block
              counts as {30 + (Number(margin) || 0)}. 0 turns it off.
            </p>
          </div>
          {error && (
            <div className="sm:col-span-3">
              <ErrorAlert message={error} />
            </div>
          )}
          <div className="sm:col-span-3">
            <Button type="submit" disabled={!dirty || busy}>
              Save waking hours
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// --- Project sessions ---

function ProjectSessionCard({
  settings,
  onSaved,
}: {
  settings: Settings;
  onSaved: (next: Settings) => void;
}) {
  const [sessionMinutes, setSessionMinutes] = useState(
    String(settings.project_session_minutes),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = Number(sessionMinutes) !== settings.project_session_minutes;

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await api.updateSettings({
          day_start: settings.day_start,
          day_end: settings.day_end,
          project_session_minutes: Number(sessionMinutes),
          task_margin_minutes: settings.task_margin_minutes,
        }),
      );
    } catch (err) {
      setError(messageOf(err, "could not save"));
    }
    setBusy(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project sessions</CardTitle>
        <CardDescription>
          A Project&apos;s duration is its remaining effort. Each time you
          schedule one, the Activity is this long and, once done, is subtracted
          from what remains.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-wrap items-end gap-4"
          onSubmit={(ev) => void handleSubmit(ev)}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="session-minutes">Session length (minutes)</Label>
            <Input
              id="session-minutes"
              className="w-32"
              type="number"
              min={1}
              max={480}
              value={sessionMinutes}
              onChange={(e) => setSessionMinutes(e.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={!dirty || busy}>
            Save session length
          </Button>
          {error && (
            <div className="basis-full">
              <ErrorAlert message={error} />
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// --- Energy Levels ---

function EnergyLevelsCard({
  levels,
  onChanged,
}: {
  levels: EnergyLevel[];
  onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAdd(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createEnergyLevel(rangeInput(name, from, to));
      setName("");
      setFrom("");
      setTo("");
      await onChanged();
    } catch (err) {
      setError(messageOf(err, "could not add Energy Level"));
    }
    setBusy(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Energy Levels</CardTitle>
        <CardDescription>
          Each level is a from–to share of your waking day to plan, the
          day&apos;s Target Load band. Inside the band a day is well planned;
          above the upper bound it is over planned. Leave &quot;from&quot; empty
          for a single figure. The highest upper bound is the default for any
          day without one. At least one is needed before a day can be planned.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {levels.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Energy Levels yet. Add one below, for example Low 40–50%, Medium
            60–75%, High 80–90%.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {levels.map((level) => (
              <EnergyLevelRow
                key={level.id}
                level={level}
                onChanged={onChanged}
              />
            ))}
          </ul>
        )}
        <Separator />
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(ev) => void handleAdd(ev)}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="energy-name">Name</Label>
            <Input
              id="energy-name"
              className="w-40"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Low"
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="energy-from">From %</Label>
            <Input
              id="energy-from"
              className="w-24"
              type="number"
              min={0}
              max={300}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="80"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="energy-to">To %</Label>
            <Input
              id="energy-to"
              className="w-24"
              type="number"
              min={1}
              max={300}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="90"
              required
            />
          </div>
          <Button type="submit" disabled={busy}>
            Add Energy Level
          </Button>
          {error && (
            <div className="basis-full">
              <ErrorAlert message={error} />
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/** Builds the save payload: an empty "from" means a single-value level. */
function rangeInput(name: string, from: string, to: string): EnergyLevelInput {
  const percent = Number(to);
  return {
    name: name.trim(),
    percent_min: from.trim() === "" ? percent : Number(from),
    percent,
  };
}

function EnergyLevelRow({
  level,
  onChanged,
}: {
  level: EnergyLevel;
  onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState(level.name);
  const [from, setFrom] = useState(
    level.percent_min === level.percent ? "" : String(level.percent_min),
  );
  const [to, setTo] = useState(String(level.percent));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = rangeInput(name, from, to);
  const dirty =
    next.name !== level.name ||
    next.percent_min !== level.percent_min ||
    next.percent !== level.percent;

  async function run(action: () => Promise<unknown>, fallback: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (err) {
      setError(messageOf(err, fallback));
    }
    setBusy(false);
  }

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={`${level.name} name`}
          className="w-40"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex items-center gap-1.5">
          <Input
            aria-label={`${level.name} from percent`}
            className="w-20"
            type="number"
            min={0}
            max={300}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            placeholder={to}
          />
          <span className="text-sm text-muted-foreground">–</span>
          <Input
            aria-label={`${level.name} to percent`}
            className="w-20"
            type="number"
            min={1}
            max={300}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
        {level.is_default && <Badge variant="secondary">Default</Badge>}
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!dirty || busy}
            onClick={() =>
              void run(
                () => api.updateEnergyLevel(level.id, next),
                "could not save",
              )
            }
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              void run(
                () => api.deleteEnergyLevel(level.id),
                "could not delete",
              )
            }
          >
            Delete
          </Button>
        </div>
      </div>
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </li>
  );
}

// --- Labels ---

/**
 * Google Calendar colour picker. Colours already used by another Label are
 * shown but disabled, so every Label keeps a distinct colour and calendar
 * colour matching stays unambiguous.
 */
function ColorSelect({
  id,
  value,
  colors,
  taken,
  onChange,
}: {
  id?: string;
  value: string;
  colors: CalendarColor[];
  /** Colour ids used by other Labels (not this row's own). */
  taken: Map<string, string>;
  onChange: (id: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="w-44" aria-label="Colour">
        <SelectValue placeholder="Pick a colour" />
      </SelectTrigger>
      <SelectContent>
        {colors.map((color) => {
          const usedBy = taken.get(color.id);
          const disabled = usedBy !== undefined && color.id !== value;
          return (
            <SelectItem key={color.id} value={color.id} disabled={disabled}>
              <span className="inline-flex items-center gap-2">
                <span
                  className="h-3 w-3 rounded-full border border-black/10"
                  style={{ backgroundColor: color.hex }}
                />
                {colorName(color)}
                {disabled && (
                  <span className="text-xs text-muted-foreground">
                    · {usedBy}
                  </span>
                )}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function LabelsCard({
  labels,
  colors,
  colorsError,
  onChanged,
}: {
  labels: LabelData[];
  colors: CalendarColor[];
  colorsError: string | null;
  onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [colorId, setColorId] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const takenByAll = new Map(labels.map((l) => [l.google_color_id, l.name]));
  const freeColors = colors.filter((c) => !takenByAll.has(c.id));

  async function handleAdd(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!colorId) {
      setError("Pick a colour.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createLabel({
        name: name.trim(),
        description: description.trim(),
        google_color_id: colorId,
      });
      setName("");
      setColorId("");
      setDescription("");
      await onChanged();
    } catch (err) {
      setError(messageOf(err, "could not add Label"));
    }
    setBusy(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Labels</CardTitle>
        <CardDescription>
          Each Label stands for a value you want to live by, coloured from
          Google Calendar&apos;s palette. Set how much of a day each one
          deserves under Ideal Day below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {colorsError && <ErrorAlert message={colorsError} />}
        {labels.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Labels yet. Templates need at least one.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {labels.map((label) => (
              <LabelRow
                key={label.id}
                label={label}
                colors={colors}
                taken={
                  new Map(
                    labels
                      .filter((l) => l.id !== label.id)
                      .map((l) => [l.google_color_id, l.name]),
                  )
                }
                onChanged={onChanged}
              />
            ))}
          </ul>
        )}
        <Separator />
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(ev) => void handleAdd(ev)}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="label-name">Name</Label>
            <Input
              id="label-name"
              className="w-40"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Health"
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="label-color">Colour</Label>
            <ColorSelect
              id="label-color"
              value={colorId}
              colors={colors}
              taken={takenByAll}
              onChange={setColorId}
            />
          </div>
          <div className="grid min-w-48 flex-1 gap-1.5">
            <Label htmlFor="label-description">Description</Label>
            <Input
              id="label-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this value means to you (optional)"
            />
          </div>
          <Button type="submit" disabled={busy || freeColors.length === 0}>
            Add Label
          </Button>
          {colors.length > 0 && freeColors.length === 0 && (
            <p className="basis-full text-xs text-muted-foreground">
              Every Google Calendar colour is already assigned to a Label. Free
              one up to add another.
            </p>
          )}
          {error && (
            <div className="basis-full">
              <ErrorAlert message={error} />
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

function LabelRow({
  label,
  colors,
  taken,
  onChanged,
}: {
  label: LabelData;
  colors: CalendarColor[];
  taken: Map<string, string>;
  onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState(label.name);
  const [colorId, setColorId] = useState(label.google_color_id);
  const [description, setDescription] = useState(label.description);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty =
    name !== label.name ||
    colorId !== label.google_color_id ||
    description !== label.description;
  const hex = hexFor(colors, colorId);

  async function run(action: () => Promise<unknown>, fallback: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (err) {
      setError(messageOf(err, fallback));
    }
    setBusy(false);
  }

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="h-4 w-4 shrink-0 rounded-full border border-black/10 bg-muted"
          style={hex ? { backgroundColor: hex } : undefined}
          aria-hidden
        />
        <Input
          aria-label={`${label.name} name`}
          className="w-40"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <ColorSelect
          value={colorId}
          colors={colors}
          taken={taken}
          onChange={setColorId}
        />
        <Input
          aria-label={`${label.name} description`}
          className="min-w-48 flex-1"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
        />
        {label.is_system && (
          <Badge
            variant="secondary"
            title={
              label.system_role === "external"
                ? "Built in: appointments you are not part of. Shown on the timeline, left out of capacity. Cannot be deleted."
                : "Built in: executed Routines count here. Cannot be deleted."
            }
          >
            System · {label.system_role}
          </Badge>
        )}
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!dirty || busy}
            onClick={() =>
              void run(
                () =>
                  api.updateLabel(label.id, {
                    name: name.trim(),
                    description: description.trim(),
                    google_color_id: colorId,
                  }),
                "could not save",
              )
            }
          >
            Save
          </Button>
          {!label.is_system && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Delete the Label "${label.name}"?`)) {
                  void run(() => api.deleteLabel(label.id), "could not delete");
                }
              }}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </li>
  );
}

// --- Ideal Day ---

/** Largest-remainder rounding so integer shares keep the same total. */
function roundToTotal(values: number[], total: number): number[] {
  const floors = values.map((v) => Math.floor(v));
  let left = total - floors.reduce((a, b) => a + b, 0);
  const order = values
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  for (const { i } of order) {
    if (left <= 0) break;
    floors[i] += 1;
    left -= 1;
  }
  return floors;
}

/**
 * Set Label i to `value` and rebalance the other Labels proportionally so
 * the shares still total 100. When the others are all 0 the difference is
 * spread evenly across them.
 */
function setShare(percents: number[], i: number, value: number): number[] {
  const n = percents.length;
  if (n === 0) return percents;
  if (n === 1) return [100];
  const target = Math.min(100, Math.max(0, Math.round(value)));
  const rest = 100 - target;
  const others = percents.map((p, j) => (j === i ? 0 : p));
  const othersTotal = others.reduce((a, b) => a + b, 0);
  const scaled =
    othersTotal === 0
      ? others.map((_, j) => (j === i ? 0 : rest / (n - 1)))
      : others.map((p) => (p / othersTotal) * rest);
  const rounded = roundToTotal(scaled, rest);
  return rounded.map((p, j) => (j === i ? target : p));
}

/** Bring stored shares to a full 100: scale them, or split evenly if all 0. */
function normalize(percents: number[]): number[] {
  const n = percents.length;
  if (n === 0) return [];
  const total = percents.reduce((a, b) => a + b, 0);
  if (total === 100) return percents;
  if (total === 0)
    return roundToTotal(
      percents.map(() => 100 / n),
      100,
    );
  return roundToTotal(
    percents.map((p) => (p / total) * 100),
    100,
  );
}

function IdealDayCard({
  labels,
  colors,
  onSaved,
}: {
  labels: LabelData[];
  colors: CalendarColor[];
  onSaved: (next: Settings) => void;
}) {
  const saved = labels.map((l) => l.ideal_percent);
  const [percents, setPercents] = useState<number[]>(() => normalize(saved));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = percents.some((p, i) => p !== saved[i]);

  async function handleSave() {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await api.updateIdealDay({
          targets: labels.map((l, i) => ({
            label_id: l.id,
            percent: percents[i],
          })),
        }),
      );
    } catch (err) {
      setError(messageOf(err, "could not save Ideal Day"));
    }
    setBusy(false);
  }

  function splitEvenly() {
    const n = labels.length;
    setPercents(
      roundToTotal(
        labels.map(() => 100 / n),
        100,
      ),
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ideal Day</CardTitle>
        <CardDescription>
          How a day&apos;s planned time should split across your Labels. Drag
          the handles between slices, or type a percentage; the other Labels
          rebalance so the shares always add up to 100. How much of the day gets
          planned at all is set by the Energy Level.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {labels.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Add Labels first, then shape your Ideal Day here.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-[minmax(0,260px)_1fr] md:items-start">
            <IdealDayDonut
              slices={labels.map((l, i) => ({
                id: l.id,
                name: l.name,
                hex: hexFor(colors, l.google_color_id),
                percent: percents[i],
              }))}
              onChange={setPercents}
            />
            <div className="space-y-3">
              <ul className="divide-y divide-border">
                {labels.map((label, i) => {
                  const hex = hexFor(colors, label.google_color_id);
                  return (
                    <li key={label.id} className="flex items-center gap-3 py-2">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full border border-black/10 bg-muted-foreground"
                        style={hex ? { backgroundColor: hex } : undefined}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {label.name}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <Input
                          aria-label={`${label.name} ideal percent`}
                          className="w-20 text-right"
                          type="number"
                          min={0}
                          max={100}
                          value={percents[i]}
                          onChange={(e) =>
                            setPercents(
                              setShare(percents, i, Number(e.target.value)),
                            )
                          }
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {error && <ErrorAlert message={error} />}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => void handleSave()}
                  disabled={!dirty || busy}
                >
                  Save Ideal Day
                </Button>
                <Button variant="outline" onClick={splitEvenly} disabled={busy}>
                  Split evenly
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setPercents(normalize(saved))}
                  disabled={!dirty || busy}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Assistant access (MCP) ---

function formatWhen(iso: string | null): string {
  if (!iso) return "never";
  return new Date(iso).toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AssistantAccessCard() {
  const [tokens, setTokens] = useState<APIToken[] | null>(null);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState<{ name: string; token: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mcpUrl = `${window.location.origin}/mcp`;

  const load = useCallback(async () => {
    try {
      setTokens((await api.tokens()).tokens);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load tokens"));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const created = await api.createToken(name.trim());
      setFresh({ name: created.name, token: created.token });
      setName("");
      await load();
    } catch (err) {
      setError(messageOf(err, "could not create token"));
    }
    setBusy(false);
  }

  async function handleRevoke(t: APIToken) {
    if (
      !window.confirm(
        `Revoke the token "${t.name}"? The assistant using it will lose access.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await api.revokeToken(t.id);
      if (fresh?.name === t.name) setFresh(null);
      await load();
    } catch (err) {
      setError(messageOf(err, "could not revoke token"));
    }
    setBusy(false);
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const snippet = fresh
    ? JSON.stringify(
        {
          mcpServers: {
            leno: {
              type: "http",
              url: mcpUrl,
              headers: { Authorization: `Bearer ${fresh.token}` },
            },
          },
        },
        null,
        2,
      )
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assistant access (MCP)</CardTitle>
        <CardDescription>
          Let an AI assistant read your Labels, Templates, and today&apos;s
          plan, add Wishlist ideas, create Templates, and schedule Activities on
          your behalf. It connects to{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">{mcpUrl}</code>{" "}
          with a personal token as a Bearer header.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <ErrorAlert message={error} />}
        {fresh && snippet && (
          <div className="space-y-2 rounded-lg border border-primary/40 bg-secondary/40 p-3">
            <p className="text-sm font-medium">
              Token for &ldquo;{fresh.name}&rdquo; — shown once, copy it now
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded bg-card px-2 py-1 text-xs">
                {fresh.token}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void copy(fresh.token)}
              >
                {copied ? "Copied" : "Copy token"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Client config, for example Claude Desktop or Claude Code:
            </p>
            <pre className="overflow-x-auto rounded bg-card p-2 text-xs">
              {snippet}
            </pre>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void copy(snippet)}
            >
              Copy config
            </Button>
          </div>
        )}
        {tokens === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No tokens yet. Create one per assistant so you can revoke them
            separately.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {tokens.map((t) => (
              <li
                key={t.id}
                className="flex flex-wrap items-center gap-2 py-2 text-sm"
              >
                <span className="min-w-0 flex-1 font-medium">{t.name}</span>
                <span className="text-xs text-muted-foreground">
                  created {formatWhen(t.created_at)} · last used{" "}
                  {formatWhen(t.last_used_at)}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void handleRevoke(t)}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
        <Separator />
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(ev) => void handleCreate(ev)}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="token-name">Token name</Label>
            <Input
              id="token-name"
              className="w-56"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Claude"
              required
            />
          </div>
          <Button type="submit" disabled={busy}>
            Create token
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
